// ============================================================
// KALKI AI FUND — SITE CONFIGURATION
// Edit this single file to update contact details, external
// trading links, master trader directory entries and CTAs.
// ============================================================

export const SITE = {
  name: "Kalki AI Fund",
  domain: "https://kalkiai.fund",
  tagline: "Intelligent Trading. Structured Capital. Transparent Performance.",
  // TODO: confirm the public enquiry mailbox before launch.
  contactEmail: "info@kalkiai.fund",
  lastUpdated: "October 2025",
};

// ---- External destinations (replace placeholders when ready) ----
export const EXTERNAL_LINKS = {
  strategyTrading: "https://www.example.com/kalki-strategies",
  vaultTrading: "https://www.example.com/kalki-vaults",
  vaultYield: "https://www.example.com/kalki-vault-yield",
};

// ---- Master trader directory (managed via this config) ----
export const MASTER_TRADERS = [
  {
    id: "mt-01",
    name: "Kalki Momentum — BTC / ETH",
    exchange: "Binance",
    style: "Trend-following · Perpetual futures",
    description:
      "Momentum-orientated master portfolio trading major crypto pairs on Binance futures.",
    url: "https://www.binance.com/en/copy-trading",
  },
  {
    id: "mt-02",
    name: "Kalki Balanced — Multi-Asset",
    exchange: "Bybit",
    style: "Multi-asset · Spot & derivatives",
    description:
      "Diversified master account combining spot accumulation with hedged derivative positions.",
    url: "https://www.bybit.com/en/copy-trading",
  },
  {
    id: "mt-03",
    name: "Kalki Systematic — Alt Matrix",
    exchange: "OKX",
    style: "Systematic rotation · Spot",
    description:
      "Rules-based rotation across a screened universe of liquid altcoin pairs on OKX.",
    url: "https://www.okx.com/copy-trading",
  },
];

// ---- Navigation ----
export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Risk Disclosure", to: "/risk-disclosure" },
  { label: "Contact Us", to: "/contact" },
];

export const OFFERING_LINKS = [
  { label: "Master Trading", to: "/master-trading" },
  { label: "Strategy Trading", to: "/strategy-trading" },
  { label: "Vault Trading", to: "/vault-trading" },
  { label: "Vault Yield", to: "/vault-yield" },
];

// ---- Home page offering cards ----
export const OFFERINGS = [
  {
    id: "master-trading",
    index: "01",
    title: "Master Trading",
    badges: ["Non-custodial", "Exchange-hosted"],
    description:
      "Discover master trading opportunities hosted on supported external exchanges, with transparent trader profiles and clear risk information.",
    features: [
      "Master trader profiles & external links",
      "Available performance and risk information",
      "Supported exchange details",
    ],
    cta: { label: "Explore Master Traders", to: "/master-trading" },
  },
  {
    id: "strategy-trading",
    index: "02",
    title: "Strategy Trading",
    badges: ["Non-custodial", "Native API integration"],
    description:
      "Algorithmic strategies designed to operate through your own exchange accounts via supported API connectivity — you retain custody throughout.",
    features: [
      "Strategy objectives & methodology",
      "Backtesting and drawdown context where available",
      "API-based execution explained end-to-end",
    ],
    cta: { label: "Explore Strategies", to: "/strategy-trading" },
  },
  {
    id: "vault-trading",
    index: "03",
    title: "Vault Trading",
    badges: ["Custodial", "Exchange-hosted"],
    description:
      "Custodial trading vaults with a clearly explained operating model, custody arrangement, trading methodology and associated risks.",
    features: [
      "Vault objectives & methodology",
      "Verified historical performance where available",
      "Custody and withdrawal disclosures",
    ],
    cta: { label: "Discover Vault Trading", to: "/vault-trading" },
  },
  {
    id: "vault-yield",
    index: "04",
    title: "Vault Yield",
    badges: ["Custodial", "Third-party audited & verified"],
    description:
      "Yield-oriented vault products supported by documented performance verification and a clearly explained promissory token and coupon structure.",
    features: [
      "Independent verification of reported results",
      "Promissory token & coupon structure",
      "Maturity, lock-in and redemption conditions",
    ],
    cta: { label: "Explore Vault Yield", to: "/vault-yield" },
  },
];

// ---- Performance transparency labels ----
export const PERFORMANCE_LABELS = [
  {
    tag: "Backtested",
    color: "#A1A1AA",
    text: "Simulated on historical data. Hypothetical results have inherent limitations and do not reflect actual trading.",
  },
  {
    tag: "Simulated",
    color: "#71717A",
    text: "Forward-tested in a simulated environment. Not live capital.",
  },
  {
    tag: "Live",
    color: "#FFC107",
    text: "Achieved with live capital on the stated platform and period.",
  },
  {
    tag: "Audited",
    color: "#22C55E",
    text: "Reviewed by an independent auditor against underlying records.",
  },
  {
    tag: "Independently verified",
    color: "#34D399",
    text: "Confirmed by a third party that is not the issuer of the product.",
  },
];

// ---- FAQs (per offering) ----
export const FAQS = {
  master: [
    {
      q: "What does “exchange-hosted” master trading mean?",
      a: "The master trader operates on the exchange's own copy-trading infrastructure. Followers allocate through the exchange itself — Kalki AI Fund does not take custody of follower funds at any point.",
    },
    {
      q: "Who controls my funds?",
      a: "You do. Master trading is non-custodial: your capital stays in your own exchange account, and you can stop following at any time through the exchange.",
    },
    {
      q: "Where do the performance figures come from?",
      a: "From the hosted exchange, for the period and account shown. We present the exchange's own published data and always label the reporting period and source. We do not modify or extrapolate it.",
    },
    {
      q: "What are the costs?",
      a: "The exchange and the master trader may charge their own profit-share or fee terms, shown on each exchange page. We publish no fee figures here until they are confirmed with the relevant provider.",
    },
  ],
  strategy: [
    {
      q: "How does API-based execution work?",
      a: "You create a dedicated API key on your exchange account with trade permission (no withdrawal permission), and the strategy engine sends order instructions through it. Orders execute on your account, in your name.",
    },
    {
      q: "Do you ever hold my assets?",
      a: "No. Strategy trading is non-custodial. We never request withdrawal permissions, and we do not take deposits, hold keys, or move client assets.",
    },
    {
      q: "Are backtested results a guarantee?",
      a: "No. Backtesting is a research tool with inherent limitations — it cannot account for every market condition, fee, or slippage scenario. Past performance, backtested or live, never guarantees future results.",
    },
    {
      q: "Which exchanges are supported?",
      a: "Supported venues depend on each strategy's venue requirements and are listed with each strategy description. Availability can change as integrations are updated.",
    },
  ],
  vault: [
    {
      q: "Is Vault Trading custodial?",
      a: "Yes. Vault Trading is a custodial arrangement: participating assets are held with the exchange or host platform under the vault's custody structure, not in your own account. This is a fundamental difference from our non-custodial offerings and is disclosed on every page of this site.",
    },
    {
      q: "How do withdrawals work?",
      a: "Under the terms of each vault, redemptions follow the host platform's custody and processing windows. Conditions, notice periods and any restrictions are disclosed in each vault's participation terms.",
    },
    {
      q: "What fees apply?",
      a: "Fee structures are published only when confirmed with the operating provider. Where terms are not yet confirmed, we say so explicitly rather than estimate.",
    },
    {
      q: "What are the main risks?",
      a: "Market risk, custodial counterparty risk, smart-contract or venue risk where applicable, and liquidity risk. Vault participation is suitable only for those who understand these risks in full.",
    },
  ],
  yield: [
    {
      q: "What is the promissory token structure?",
      a: "Participation is represented by a token evidencing a contractual promise — the issuer's obligation — with coupon terms and maturity conditions set out in the product documentation. It is not a deposit and not a share of any fund's equity.",
    },
    {
      q: "Are yields guaranteed?",
      a: "No. Target or projected yields are not guarantees. Only independently verified historical performance is presented as fact, clearly dated and sourced. Redemption depends on issuer performance and solvency — default risk exists and is disclosed.",
    },
    {
      q: "Who verifies the performance?",
      a: "An independent third-party verifier reviews reported results against underlying records. Verification reports are published only when the supporting documentation exists — until then, we show an informational placeholder instead of numbers.",
    },
    {
      q: "What are the lock-in and maturity conditions?",
      a: "Each issuance defines its own maturity date, lock-in window and redemption process. Confirmed terms are provided in the offering documentation; anything unconfirmed is labelled as such on this site.",
    },
  ],
};

// ---- Legal footer ----
export const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms and Conditions", to: "/terms-and-conditions" },
  { label: "Performance Methodology", to: "/performance-methodology" },
  { label: "Custody and Asset Handling", to: "/custody-and-asset-handling" },
  { label: "Fees and Product Terms", to: "/fees-and-product-terms" },
];

export const RISK_DISCLAIMER =
  "Digital asset trading involves substantial risk of loss and is not suitable for every investor. Past performance, backtested or otherwise, is not indicative of future results. Nothing on this website constitutes investment, legal or tax advice, an offer, or a solicitation to buy or sell any asset. All product descriptions, custody claims and yield terms must be reviewed for applicable legal and regulatory requirements before publication.";